import React, { useState } from 'react';
import './search-statement-dialog.css';
import Tooltip from '../../../lib/tooltip/Tooltip';
import { IconButton } from '@mui/material';
import {
	SearchStatementBuilderTabType,
	useSearchStatementContext
} from './search-statement-context';
import InlineInputMap from './InlineInputMap';
import DataSourceSelection from './DataSourceSelection';
import {
	getDatabaseEntity,
	searchStatementDefs,
	searchStatementState as searchStatementStateLib
} from '@elyx-code/project-logic-tree';

const { AggregationStatement, NestedSearchStatement } = searchStatementStateLib;

const { DataSourceType } = searchStatementDefs;

type NestedSearchStatement = searchStatementStateLib.NestedSearchStatement;
type AggregationStatement = searchStatementStateLib.AggregationStatement;
type DataSourceType = searchStatementDefs.DataSourceType;

export interface IMainSourceAndInputsSectionProps {
	hideInputs?: boolean;
}

const MainSourceAndInputsSection: React.FC<
	IMainSourceAndInputsSectionProps
> = ({ hideInputs }) => {
	const {
		mainDataSource,
		setMainDataSource,
		dataSources,
		tabManager,
		entity
	} = useSearchStatementContext();

	const useForceUpdate = () => {
		const [value, setValue] = useState(0);
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	return (
		<section
			style={{
				display: 'flex',
				flexDirection: 'column',
				marginLeft: 80,
				paddingRight: 40,
				marginTop: 20,
				// minWidth: 0 lets the inner inputs row collapse and scroll
				// horizontally instead of forcing this column flex (and the
				// sections below) to grow with the inputs' total width.
				minWidth: 0
			}}
		>
			{!hideInputs && (
				<>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 16
						}}
					>
						<h3
							style={{ margin: 0, fontSize: 12, fontWeight: 500 }}
						>
							Input values
						</h3>
						<IconButton
							id="search-statement-dialog-add-input-value-button"
							aria-label="add-input-value"
							size="small"
							disabled={!entity.editable}
							style={{
								padding: 4,
								fontSize: 14,
								width: 22,
								height: 22
							}}
							onClick={() => {
								tabManager.addTab(
									{
										entity: SearchStatementBuilderTabType.NewInputTab
									},
									true
								);
							}}
						>
							<i className="fa-solid fa-plus" />
						</IconButton>
						<Tooltip
							anchorQuerySelector="#search-statement-dialog-add-input-value-button"
							placement="bottom"
							openDelay={300}
						>
							Add a new input value from the canvas logic
							<br />
							You can give it a dynamic value or a fixed value to
							use within the statement builder
						</Tooltip>
					</div>

					{!!entity?.inputs.length && (
						<br className="quarter-space" />
					)}

					<div
						style={{
							display: 'flex',
							gap: 8,
							// Contain horizontal overflow inside this row only so a
							// long list of inputs doesn't push the sections below
							// (Data aggregation, Filter conditions, Output format) to
							// the right. paddingBottom leaves room for the scrollbar.
							overflowX: 'auto',
							padding: 4,
							paddingBottom: 8
						}}
					>
						{entity?.inputs.map((input) => (
							<InlineInputMap
								key={input.id}
								input={input}
								onEdit={() => {
									// Open on new tab
									tabManager.addTab(
										{
											entity: SearchStatementBuilderTabType.InputDeclarationTab,
											input: input.declaration
										},
										true
									);
								}}
								onDelete={() => {
									forceUpdate();
								}}
							/>
						))}
					</div>

					<br />
				</>
			)}

			<DataSourceSelection
				options={dataSources.filter(
					(source) => source.sourceType !== DataSourceType.Aggregation
				)}
				onChange={(dataSource) => {
					setMainDataSource(dataSource);
				}}
				alias={mainDataSource?.as}
				onAliasChange={(alias) => {
					if (mainDataSource?.sourceType === DataSourceType.Table) {
						mainDataSource.as = alias;
					} else if (
						mainDataSource?.sourceType === DataSourceType.Query
					) {
						(mainDataSource.source as NestedSearchStatement).as =
							alias;
					} else if (
						mainDataSource?.sourceType ===
						DataSourceType.Aggregation
					) {
						(mainDataSource.source as AggregationStatement).as =
							alias;
					}

					setMainDataSource(mainDataSource);
				}}
				getOptionDisabled={(option) => {
					if (option.sourceType === DataSourceType.Table) {
						// If the entity is not part of the same database as the main data source,
						// disable the option
						const assignedDatabaseEntity = getDatabaseEntity(
							option.entity
						);

						console.log(
							'assignedDatabaseEntity',
							assignedDatabaseEntity?.name
						);

						if (!assignedDatabaseEntity) {
							return true;
						}

						if (!mainDataSource) {
							return false;
						}

						const mainDataSourceDatabaseEntity = getDatabaseEntity(
							mainDataSource?.entity
						);

						console.log(
							'mainDataSourceDatabaseEntity',
							mainDataSourceDatabaseEntity?.name
						);

						if (
							mainDataSourceDatabaseEntity &&
							assignedDatabaseEntity?.id !==
								mainDataSourceDatabaseEntity?.id
						) {
							return true;
						}
					}

					return false;
				}}
				getTooltip={(option) => {
					// Return simple tooltip, or the disabled reason
					// If the entity is not part of the same database as the main data source,
					// disable the option
					if (option.sourceType === DataSourceType.Table) {
						const assignedDatabaseEntity = getDatabaseEntity(
							option.entity
						);

						if (!assignedDatabaseEntity) {
							return (
								<>
									This entity is not part of a database
									<br />
									Please assign a parent database to this
									entity on the canvas
								</>
							);
						}

						if (!mainDataSource) {
							return '';
						}

						const mainDataSourceDatabaseEntity = getDatabaseEntity(
							mainDataSource?.entity
						);

						if (
							mainDataSourceDatabaseEntity &&
							assignedDatabaseEntity?.id !==
								mainDataSourceDatabaseEntity?.id
						) {
							return (
								<>
									This entity is part of a different database
									than the currently selected main data source
									<br />
									All data sources must be part of the same
									database
								</>
							);
						}
					}

					return '';
				}}
				value={mainDataSource}
				id="search-statement-dialog-data-source-select"
			/>
		</section>
	);
};

export default MainSourceAndInputsSection;
